import {inject, Injectable} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {finalize, switchMap, tap} from 'rxjs/operators';

import {Payment} from '../../domain/entities/payment.entity';
import {PAYMENT_FEEDBACK} from '../../domain/ports/ui/feedback.port';
import {PaymentQueryService} from '../stores/charge/payment-query.service';
import {PaymentCommandService} from '../stores/charge/payment-command.service';
import {PaymentVerificationService} from '../services/payment-verification.service';
import {PaymentError, PaymentPollingCancelledError} from '../../domain/model/payment.errors';
import {PAYMENT_VERIFICATION_RECOVERY} from '../../domain/ports/ui/recovery.port';
import {CheckoutOrder} from '@platform/checkout/domain/models/checkout-order.model';

@Injectable({providedIn: 'root'})
export class ResumePaymentUseCase {
  private readonly feedback = inject(PAYMENT_FEEDBACK);
  private readonly paymentQuery = inject(PaymentQueryService);
  private readonly paymentCmd = inject(PaymentCommandService);
  private readonly recovery = inject(PAYMENT_VERIFICATION_RECOVERY);
  private readonly verificationService = inject(PaymentVerificationService);

  execute({order}: { order: CheckoutOrder }): Observable<Payment> {
    const existing = this.paymentQuery.getByOrderId(order.id);

    if (!existing?.transactionId || !existing?.method) {
      return throwError(() => new Error('[ResumePayment] Missing payment data for order'));
    }

    this.paymentCmd.markProcessing(existing);

    return this.recovery.recoverVerification(existing, null).pipe(
      tap(confirmed => confirmed && this.feedback.showLoading()),
      switchMap(confirmed => confirmed
        ? this.verificationService.execute(existing)
        : throwError(() => new PaymentPollingCancelledError())),
      tap({error: (error) => this.feedback.showError(PaymentError.extractKey(error))}),
      finalize(() => this.feedback.hideLoading())
    );
  }
}
